// 2026-10-04 addition — see ADMIN_RCM_CONSOLIDATION_PLAN.md. The 3 charts
// for the consolidated RCM dashboard. Chart.js registration pattern copied
// verbatim from labariumaiportal's own real, already-working TrendsPanel.js
// (ChartJS.register(...) call) — not guessed at, that app already proved
// this exact setup works.
import React, { useRef } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Tooltip as ChartTooltip,
  Legend as ChartLegend,
} from "chart.js";
import { Bar, Doughnut } from "react-chartjs-2";
import { Box, Typography, IconButton, Tooltip, useTheme } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, ChartTooltip, ChartLegend);

const B = { navy: "#00357a", gold: "#c9a96e", green: "#1a6b3a", red: "#c0392b", slate: "#64748b" };

// 2026-10-04 — download-as-image. react-chartjs-2 forwards a ref straight
// to the underlying Chart.js instance (not a DOM node) when passed via the
// `ref` prop on <Bar>/<Doughnut> — Chart.js's own `.toBase64Image()` does
// the actual PNG export, a real built-in method, not a workaround.
function downloadChartImage(chartRef, filename) {
  if (!chartRef.current) return;
  const url = chartRef.current.toBase64Image("image/png", 1);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
}

// Same gradient-header card convention as RequisitionConversion.js's own
// SectionCard — not a new pattern, matching this app's real design
// language rather than a plain bordered box.
function ChartCard({ title, children, isDark, onDownload }) {
  return (
    <Box sx={{
      flex: "1 1 380px", minWidth: 340, borderRadius: "14px", overflow: "hidden",
      border: `1px solid ${isDark ? "rgba(201,169,110,0.18)" : "rgba(0,53,122,0.12)"}`,
      bgcolor: isDark ? "rgba(17,24,39,0.6)" : "#fff",
      boxShadow: isDark ? "none" : "0 2px 16px rgba(0,31,77,0.05)",
    }}>
      <Box sx={{
        px: 2.5, py: 1.3, background: isDark
          ? "linear-gradient(135deg, rgba(0,53,122,0.25), rgba(201,169,110,0.08))"
          : "linear-gradient(135deg, rgba(0,53,122,0.05), rgba(201,169,110,0.05))",
        borderBottom: `1px solid ${isDark ? "rgba(201,169,110,0.15)" : "rgba(0,53,122,0.1)"}`,
        display: "flex", alignItems: "center", justifyContent: "space-between",
      }}>
        <Typography sx={{ fontSize: 13.5, fontWeight: 700, letterSpacing: "0.04em", color: isDark ? "#dfc08a" : "#00357a", fontFamily: "'Inter', sans-serif" }}>
          {title.toUpperCase()}
        </Typography>
        {onDownload && (
          <Tooltip title="Download chart as PNG">
            <IconButton size="small" onClick={onDownload} sx={{ color: isDark ? "#dfc08a" : "#00357a", p: 0.4 }}>
              <DownloadIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      <Box sx={{ p: 2.5, height: 340, position: "relative" }}>{children}</Box>
    </Box>
  );
}

function centsToDollars(cents) {
  return Math.round((cents || 0) / 100);
}

export default function RcmCharts({ stats }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const tickColor = isDark ? "#94a3b8" : "#64748b";
  const gridColor = isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)";
  const revenueChartRef = useRef(null);
  const statusChartRef = useRef(null);
  const practiceChartRef = useRef(null);

  if (!stats) return null;

  const revenueEntries = Object.entries(stats.monthly_revenue_cents || {});
  const revenueData = {
    labels: revenueEntries.map(([month]) => month),
    datasets: [{
      label: "Revenue ($)",
      data: revenueEntries.map(([, cents]) => centsToDollars(cents)),
      backgroundColor: B.navy,
      borderRadius: 4,
    }],
  };

  const statusCounts = stats.payment_status_breakdown || { completed: 0, pending: 0, failed: 0 };
  const statusData = {
    labels: ["Completed", "Pending", "Failed"],
    datasets: [{
      data: [statusCounts.completed, statusCounts.pending, statusCounts.failed],
      backgroundColor: [B.green, B.gold, B.red],
      borderWidth: 0,
    }],
  };

  const practiceEntries = Object.entries(stats.clinician_counts_by_practice || {}).slice(0, 10);
  const practiceData = {
    labels: practiceEntries.map(([name]) => name),
    datasets: [{
      label: "Clinicians registered",
      data: practiceEntries.map(([, count]) => count),
      backgroundColor: B.slate,
      borderRadius: 4,
    }],
  };

  const axisOpts = {
    ticks: { color: tickColor, font: { size: 10.5 } },
    grid: { color: gridColor },
  };
  const barOptions = {
    responsive: true, maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: { x: axisOpts, y: { ...axisOpts, beginAtZero: true } },
  };
  const doughnutOptions = {
    responsive: true, maintainAspectRatio: false,
    plugins: { legend: { position: "bottom", labels: { color: tickColor, font: { size: 11 }, padding: 12 } } },
  };

  return (
    <Box sx={{ display: "flex", gap: 2.5, flexWrap: "wrap", mb: 3 }}>
      <ChartCard title="Monthly Revenue" isDark={isDark}
        onDownload={revenueEntries.length ? () => downloadChartImage(revenueChartRef, "monthly_revenue.png") : undefined}>
        {revenueEntries.length ? <Bar ref={revenueChartRef} data={revenueData} options={barOptions} /> : <EmptyChart text="No completed payments yet" />}
      </ChartCard>
      <ChartCard title="Payment Status Breakdown" isDark={isDark}
        onDownload={(statusCounts.completed + statusCounts.pending + statusCounts.failed) > 0 ? () => downloadChartImage(statusChartRef, "payment_status_breakdown.png") : undefined}>
        {(statusCounts.completed + statusCounts.pending + statusCounts.failed) > 0
          ? <Doughnut ref={statusChartRef} data={statusData} options={doughnutOptions} />
          : <EmptyChart text="No patients yet" />}
      </ChartCard>
      <ChartCard title="Clinicians by Practice" isDark={isDark}
        onDownload={practiceEntries.length ? () => downloadChartImage(practiceChartRef, "clinicians_by_practice.png") : undefined}>
        {practiceEntries.length ? <Bar ref={practiceChartRef} data={practiceData} options={barOptions} /> : <EmptyChart text="No clinicians registered yet" />}
      </ChartCard>
    </Box>
  );
}

function EmptyChart({ text }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: "text.disabled", fontSize: 14 }}>
      {text}
    </Box>
  );
}
